import { Link } from '../components/SmartLink';
import { useParams } from 'react-router-dom';
import {
  Clock, Tag, ArrowLeft, ArrowRight, WhatsappLogo,
  Lightbulb, Warning,
} from '@phosphor-icons/react';
import SEO from '../components/SEO';
import NotFound from './NotFound';
import { breadcrumbSchema, articleSchema, faqSchema } from '../lib/seoSchemas';
import { FAQSection } from '../components/design-system';
import { buildWAUrl } from '../lib/whatsapp';
import { getPost, getRelated, heroFor, CATEGORIES, type Block, type BlogPost as Post } from '../data/blogPosts';

const CAT_LABEL: Record<string, string> = Object.fromEntries(CATEGORIES.map(c => [c.id, c.label]));

const CTA_CONFIG: Record<Post['ctaType'], { title: string; sub: string; label: string; href: string; wa: string }> = {
  visa:      { title: 'Need help with a visa?', sub: 'Our UAE-based visa team handles the paperwork and processing for you.', label: 'Visa services', href: '/visas', wa: 'Hi Big Bird Travel, I need help with a visa application.' },
  package:   { title: 'Ready to plan your holiday?', sub: 'Tell us your dates and budget — we will build a package that fits.', label: 'Holiday packages', href: '/tour-packages', wa: 'Hi Big Bird Travel, I would like a holiday package quote.' },
  umrah:     { title: 'Planning your Umrah?', sub: 'Flights, visa and hotels near the Haram, arranged by our team.', label: 'Umrah packages', href: '/umrah', wa: 'Hi Big Bird Travel, I would like an Umrah package quote.' },
  tour:      { title: 'Book a UAE experience', sub: 'Desert safari, dhow cruise, city tours and more across the Emirates.', label: 'UAE tours', href: '/uae-tours', wa: 'Hi Big Bird Travel, I would like to book a UAE tour.' },
  corporate: { title: 'Set up corporate travel for your company', sub: 'One managed account, negotiated fares and consolidated invoicing from our Mussafah, Abu Dhabi team.', label: 'Corporate travel', href: '/corporate-travel', wa: 'Hi Big Bird Travel, we would like to set up a corporate travel account for our company.' },
};

function formatDate(dateStr: string) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function CtaBlock({ type }: { type: Post['ctaType'] }) {
  const c = CTA_CONFIG[type];
  return (
    <div className="my-9 rounded-2xl border border-[#D8EBFF] bg-[#F5FAFF] p-6 md:p-7">
      <h3 className="font-display text-[19px] font-black text-[#071126]">{c.title}</h3>
      <p className="mt-2 text-[14px] leading-relaxed text-[#5F6B7A]">{c.sub}</p>
      <div className="mt-4 flex flex-wrap gap-3">
        <a href={buildWAUrl(c.wa)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-[#0F7A35] px-5 py-3 text-[13px] font-black uppercase tracking-wide text-white transition hover:-translate-y-0.5">
          <WhatsappLogo size={16} weight="fill" /> WhatsApp us
        </a>
        <Link to={c.href} className="inline-flex items-center gap-2 rounded-xl border border-[#0068B3] px-5 py-3 text-[13px] font-black uppercase tracking-wide text-[#0068B3] transition hover:bg-[#0068B3] hover:text-white">
          {c.label} <ArrowRight size={15} weight="bold" />
        </Link>
      </div>
    </div>
  );
}

function renderBlock(b: Block, i: number, ctaType: Post['ctaType']) {
  switch (b.t) {
    case 'p':   return <p key={i} className="mb-5 text-[15.5px] leading-[1.75] text-[#374151]">{b.v}</p>;
    case 'h2':  return <h2 key={i} className="mb-4 mt-10 font-display text-[26px] font-black text-[#071126]" style={{ letterSpacing: '-0.02em' }}>{b.v}</h2>;
    case 'h3':  return <h3 key={i} className="mb-3 mt-7 font-display text-[19px] font-black text-[#071126]">{b.v}</h3>;
    case 'ul':  return <ul key={i} className="mb-6 space-y-2.5">{b.v.map((li, j) => (
        <li key={j} className="flex items-start gap-3 text-[15px] leading-relaxed text-[#374151]"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#0096FF]" />{li}</li>
      ))}</ul>;
    case 'ol':  return <ol key={i} className="mb-6 space-y-2.5">{b.v.map((li, j) => (
        <li key={j} className="flex items-start gap-3 text-[15px] leading-relaxed text-[#374151]"><span className="mt-0.5 shrink-0 font-display text-[13px] font-black text-[#0068B3]">{j + 1}.</span>{li}</li>
      ))}</ol>;
    case 'tip': return (
      <div key={i} className="my-7 flex gap-3 rounded-xl border border-[#CDE9D6] bg-[#EAF7EE] p-5">
        <Lightbulb size={22} weight="fill" className="mt-0.5 shrink-0 text-[#0F7A35]" />
        <div>{b.heading && <p className="mb-1 font-display text-[14px] font-black text-[#0F5B2A]">{b.heading}</p>}<p className="text-[14px] leading-relaxed text-[#2F5540]">{b.v}</p></div>
      </div>
    );
    case 'warn': return (
      <div key={i} className="my-7 flex gap-3 rounded-xl border border-[#F3D9BC] bg-[#FDF3E7] p-5">
        <Warning size={22} weight="fill" className="mt-0.5 shrink-0 text-[#B5641A]" />
        <div>{b.heading && <p className="mb-1 font-display text-[14px] font-black text-[#8A4A12]">{b.heading}</p>}<p className="text-[14px] leading-relaxed text-[#6E4A22]">{b.v}</p></div>
      </div>
    );
    case 'linkgrid': return (
      <div key={i} className="my-8 rounded-2xl border border-[#E2E8F2] bg-[#F8FAFD] p-5">
        {b.heading && <p className="mb-3 text-[11px] font-black uppercase tracking-[0.2em] text-[#0068B3]">{b.heading}</p>}
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {b.items.map((it, j) => (
            <Link key={j} to={it.href} className="group flex items-center justify-between gap-2 rounded-xl border border-[#E2E8F2] bg-white px-4 py-3 transition hover:border-[#0096FF]">
              <span className="text-[14px] font-bold text-[#0B2F5A]">{it.text}</span>
              <ArrowRight size={15} weight="bold" className="shrink-0 text-[#0068B3] transition group-hover:translate-x-1" />
            </Link>
          ))}
        </div>
      </div>
    );
    case 'table': return (
      <div key={i} className="my-7 overflow-x-auto">
        <table className="w-full border-collapse text-[13.5px]">
          <thead>
            <tr>{b.headers.map((h, j) => <th key={j} className="border-b-2 border-[#0068B3] bg-[#F5FAFF] px-3 py-2.5 text-left font-display font-black text-[#071126]">{h}</th>)}</tr>
          </thead>
          <tbody>
            {b.rows.map((row, r) => (
              <tr key={r} className={r % 2 ? 'bg-[#FAFCFF]' : ''}>
                {row.map((cell, c) => <td key={c} className="border-b border-[#EDF1F7] px-3 py-2.5 text-[#374151]">{cell}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
    case 'cta': return <CtaBlock key={i} type={ctaType} />;
    default: return null;
  }
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const post = getPost(slug ?? '');
  if (!post) return <NotFound />;

  const related = getRelated(post.slug, 3);
  const heroImage = heroFor(post);

  return (
    <div className="bg-white">
      <SEO
        title={post.seoTitle}
        description={post.metaDescription}
        canonical={`/blog/${post.slug}`}
        skipSuffix
        ogType="article"
        ogImage={heroImage.startsWith('http') ? heroImage : `https://bigbirdtravel.com${heroImage}`}
        schema={[
          articleSchema({
            headline: post.title,
            description: post.metaDescription,
            url: `/blog/${post.slug}`,
            image: heroImage.startsWith('http') ? heroImage : `https://bigbirdtravel.com${heroImage}`,
            datePublished: post.publishDate,
            authorName: post.author,
          }),
          faqSchema(post.faqs),
          breadcrumbSchema([
            { name: 'Home', item: '/' },
            { name: 'Blog', item: '/blog' },
            { name: post.title, item: `/blog/${post.slug}` },
          ]),
        ]}
      />

      {/* Hero image */}
      {post.heroBanner ? (
        // Designed banner with baked-in text — show the whole image, uncropped.
        <div className="w-full bg-[#EAF2FB]">
          <img src={heroImage} alt={post.title} className="mx-auto block h-auto w-full max-w-5xl" loading="eager" />
        </div>
      ) : (
        <div className="relative h-64 w-full overflow-hidden md:h-96">
          <img src={heroImage} alt={post.title} className="absolute inset-0 h-full w-full object-cover" loading="eager" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(7,17,38,0.6), rgba(7,17,38,0.1))' }} />
        </div>
      )}

      <article className="mx-auto max-w-3xl px-6 py-10 lg:py-14">
        <nav className="mb-5 flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#7C89A0]">
          <Link to="/" className="hover:text-[#0068B3]">Home</Link><span>/</span>
          <Link to="/blog" className="hover:text-[#0068B3]">Blog</Link><span>/</span>
          <span className="text-[#0068B3]">{CAT_LABEL[post.category] ?? post.category}</span>
        </nav>

        <div className="mb-4 flex flex-wrap items-center gap-3 text-[11px] font-black uppercase tracking-widest text-[#0068B3]">
          <span className="inline-flex items-center gap-1"><Tag size={12} weight="fill" /> {CAT_LABEL[post.category] ?? post.category}</span>
          <span className="text-gray-300">|</span>
          <span className="inline-flex items-center gap-1 text-gray-400"><Clock size={12} /> {post.readingTime} min read</span>
          <span className="text-gray-300">|</span>
          <span className="text-gray-400">{formatDate(post.publishDate)}</span>
        </div>

        <h1 className="font-display text-[32px] font-black leading-[1.12] text-[#071126] md:text-[40px]" style={{ letterSpacing: '-0.025em' }}>{post.title}</h1>
        <p className="mt-3 text-[13px] font-medium text-gray-400">By {post.author}</p>

        <p className="my-8 border-l-4 border-[#0096FF] pl-5 text-[16px] font-medium italic leading-relaxed text-[#5F6B7A]">{post.excerpt}</p>

        <div>{post.content.map((b, i) => renderBlock(b, i, post.ctaType))}</div>

        {/* Related service links */}
        {post.relatedLinks.length > 0 && (
          <div className="mt-10 rounded-2xl border border-[#E2E8F2] bg-[#F8FAFD] p-6">
            <p className="mb-3 text-[11px] font-black uppercase tracking-[0.2em] text-[#0068B3]">Related services</p>
            <div className="flex flex-wrap gap-2.5">
              {post.relatedLinks.map((l, i) => (
                <Link key={i} to={l.href} className="inline-flex items-center gap-1.5 rounded-full border border-[#D8EBFF] bg-white px-4 py-2 text-[13px] font-bold text-[#0068B3] transition hover:bg-[#0068B3] hover:text-white">
                  {l.text} <ArrowRight size={13} weight="bold" />
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 border-t border-gray-100 pt-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-[12px] font-black uppercase tracking-widest text-[#0068B3] hover:text-[#071126]">
            <ArrowLeft size={13} weight="bold" /> All guides
          </Link>
        </div>
      </article>

      {post.faqs.length > 0 && (
        <FAQSection eyebrow="FAQ" title="Frequently asked questions" faqs={post.faqs} />
      )}

      {/* Related posts */}
      {related.length > 0 && (
        <section className="px-6 py-12 lg:py-14 bg-[#F5FAFF]">
          <div className="mx-auto max-w-6xl">
            <p className="mb-6 text-[11px] font-black uppercase tracking-[0.2em] text-[#0068B3]">Keep reading</p>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map(r => (
                <Link key={r.slug} to={`/blog/${r.slug}`} className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[#E2E8F2] bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-[#0096FF] hover:shadow-lg">
                  <div className="relative h-40 overflow-hidden">
                    <img src={heroFor(r)} alt={r.title} className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105" loading="lazy" />
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <span className="mb-2 text-[10px] font-black uppercase tracking-wider text-[#0068B3]">{CAT_LABEL[r.category] ?? r.category}</span>
                    <h3 className="font-display text-[16px] font-black leading-snug text-[#071126]">{r.title}</h3>
                    <span className="mt-auto inline-flex items-center gap-1 pt-4 text-[12px] font-black uppercase tracking-wide text-[#0068B3]">Read <ArrowRight size={13} weight="bold" className="transition group-hover:translate-x-1" /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
